// Each property's primary offering, keyed by property id.
//
// Published for the orders module (architecture-principles §10 — shared-endpoint
// hooks). An order row names only its property, so any orders screen that groups
// by offering — the approval queue does — has no way to say what state that
// offering is in without asking here.
//
// One request, not one per property: the consumer is a queue table with tens of
// rows, and a fan-out would put a request pair behind every one of them.

import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'

import { getAllOfferingsForLookup } from '../offeringPropertyEndpoints'
import { offeringPropertyQueryKeys } from '../offeringPropertyQueryKeys'
import { OFFERING_TYPE } from '../../constants/offeringStatus'
import type { OfferingListItem } from '../../types/offeringTypes'

/** What a consumer grouping by property needs to know about its offering. */
export interface PrimaryOfferingSummary {
  offeringId: string
  propertyId: string
  offeringTitle: string
  /** Approval lifecycle — draft / in_verification / approved / rejected / … */
  offeringStatus: string
  /** Funding lifecycle — pending / open / filled / completed. Independent axis. */
  offeringProcessingStatus: string
  investmentWindowStart: string
  /** Window close. An unfilled offering past this reads as Expired. */
  investmentWindowEnd: string
  /** When the offering was created — what "the latest offering" is ordered by. */
  createdAt: string
}

function toSummary(row: OfferingListItem): PrimaryOfferingSummary {
  return {
    offeringId: row.id,
    propertyId: row.propertyId,
    offeringTitle: row.offeringTitle ?? '',
    offeringStatus: row.offeringStatus ?? '',
    offeringProcessingStatus: row.offeringProcessingStatus ?? '',
    investmentWindowStart: row.investmentWindowStart ?? '',
    investmentWindowEnd: row.investmentWindowEnd ?? '',
    createdAt: row.createdAt ?? '',
  }
}

export function usePrimaryOfferingsByPropertyQuery() {
  const query = useQuery({
    queryKey: () => offeringPropertyQueryKeys.offeringsLookup(),
    queryFn: () => getAllOfferingsForLookup(),
    // An offering's status moves on operator action elsewhere, not on its own;
    // a consuming queue re-reading it every minute would be noise.
    staleTime: 1000 * 60 * 5,
  })

  /**
   * Which of a property's offerings represents it.
   *
   * Primary wins outright. Otherwise the newest stands in — the sweep returns
   * rows newest-first, so an offering the backend left untyped still resolves
   * instead of leaving the property blank. Selecting here rather than filtering
   * the request by type is what makes that fallback possible at all.
   */
  const offeringsByPropertyId = computed(() => {
    const map = new Map<string, PrimaryOfferingSummary>()
    const primaryKeys = new Set<string>()

    for (const row of query.data.value ?? []) {
      if (!row.propertyId) continue
      const isPrimary = row.offeringType === OFFERING_TYPE.PRIMARY

      // A primary offering always wins. Among non-primary rows the first seen
      // wins, and the sweep is newest-first, so that is the most recent one.
      if (primaryKeys.has(row.propertyId)) continue
      if (map.has(row.propertyId) && !isPrimary) continue

      map.set(row.propertyId, toSummary(row))
      if (isPrimary) primaryKeys.add(row.propertyId)
    }
    return map
  })

  return { offeringsByPropertyId, isLoading: query.isLoading, isError: query.isError }
}
